import { useState, useEffect } from "react";
import { Sparkles, CheckCircle, AlertTriangle, Lightbulb, ArrowRight, Loader2 } from "lucide-react";
import { useAITutor, type ModuleFeedbackData } from "@/hooks/useAITutor";

const FALLBACK: ModuleFeedbackData = {
  what_went_well: "You completed the simulation and made decisions under realistic pressure.",
  weakening_decision: "Some choices prioritized short-term comfort over long-term stability.",
  concept_applied: "The simulation tested your ability to apply financial reasoning to real scenarios.",
  hidden_tradeoff: "Every financial decision has an opportunity cost — what you didn't choose matters too.",
  do_differently: "Next time, consider the second-order effects of each decision before committing.",
  related_lesson_concepts: [],
};

interface Props {
  moduleName: string;
  moduleKey: string;
  finalMetrics?: Record<string, unknown>;
  keyDecisions?: Record<string, unknown>[];
  outcome?: string;
  ageBracket?: string;
}

export default function ModuleFeedbackCard({
  moduleName, moduleKey, finalMetrics, keyDecisions, outcome, ageBracket,
}: Props) {
  const { loading, getModuleFeedback } = useAITutor();
  const [data, setData] = useState<ModuleFeedbackData | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      const result = await getModuleFeedback({ moduleName, moduleKey, finalMetrics, keyDecisions, outcome, ageBracket });
      if (cancelled) return;
      if (result.fallback || !result.data) setUseFallback(true);
      else setData(result.data);
    };
    fetch();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  const content = data || (useFallback ? FALLBACK : null);

  return (
    <div className="rounded-2xl border border-accent/15 bg-card/90 backdrop-blur-md p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-accent" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-sm text-foreground">AI Strategy Debrief</h3>
          <p className="text-[10px] text-muted-foreground">{moduleName}</p>
        </div>
      </div>

      {loading && !content ? (
        <div className="flex items-center justify-center py-6 gap-2">
          <Loader2 className="w-5 h-5 text-accent animate-spin" />
          <span className="text-sm text-muted-foreground">Analyzing your strategy…</span>
        </div>
      ) : content ? (
        <>
          {/* What went well */}
          <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400">What Went Well</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.what_went_well}</p>
          </div>

          {/* Weakening decision */}
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400">Key Weakness</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.weakening_decision}</p>
          </div>

          {/* Concept applied */}
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Concept in Action</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.concept_applied}</p>
          </div>

          {/* Hidden tradeoff */}
          <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Hidden Tradeoff</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.hidden_tradeoff}</p>
          </div>

          {/* What to do differently */}
          <div className="flex items-start gap-2">
            <ArrowRight className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Next time: </span>
              {content.do_differently}
            </p>
          </div>

          {/* Related lesson concepts */}
          {content.related_lesson_concepts.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {content.related_lesson_concepts.map((concept, i) => (
                <span key={i} className="text-[10px] font-heading font-semibold px-2 py-0.5 rounded-full bg-muted/40 border border-border/50 text-muted-foreground">
                  {concept}
                </span>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
